import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, FilePen, Award, PartyPopper, AlertTriangle, Eye, Settings, Car, ShieldCheck, Key } from "lucide-react";
import { EmploymentContractForm } from "./EmploymentContractForm";
import { ContractAmendmentForm } from "./ContractAmendmentForm";
import { CertificateForm } from "./CertificateForm";
import { AnniversaryCertificateForm } from "./AnniversaryCertificateForm";
import { WarningForm } from "./WarningForm";
import { VehicleAssignmentForm } from "./VehicleAssignmentForm";
import { SafetyInstructionForm } from "./SafetyInstructionForm";
import { KeyHandoverForm } from "./KeyHandoverForm";
import { ContractsList } from "./ContractsList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TemplatePreview } from "./TemplatePreview";
import { DocumentTemplateEditor } from "./DocumentTemplateEditor";
import { useAuth } from "@/hooks/useAuth";

export const DocumentTemplatesSettings = () => {
  const { userRole } = useAuth();
  const [activeTab, setActiveTab] = useState("employment");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<string>("");
  
  // hr_clerk can edit templates but cannot see created documents (ContractsList)
  const canSeeContracts = userRole !== 'hr_clerk';

  const handleOpenPreview = (type: string) => {
    setPreviewType(type);
    setPreviewOpen(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dokumentvorlagen
          </CardTitle>
          <CardDescription>
            Erstellen Sie Arbeitsverträge, Vertragsänderungen, Zertifikate, Jubiläumsurkunden und Abmahnungen.
            Die erstellten Dokumente werden automatisch in der Mitarbeiterakte abgelegt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-9 h-auto gap-1">
              <TabsTrigger value="employment" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Arbeitsvertrag</span>
                <span className="sm:hidden">Vertrag</span>
              </TabsTrigger>
              <TabsTrigger value="amendment" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <FilePen className="h-4 w-4" />
                <span className="hidden sm:inline">Änderung</span>
                <span className="sm:hidden">Änder.</span>
              </TabsTrigger>
              <TabsTrigger value="certificate" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <Award className="h-4 w-4" />
                <span className="hidden sm:inline">Zertifikat</span>
                <span className="sm:hidden">Zert.</span>
              </TabsTrigger>
              <TabsTrigger value="anniversary" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <PartyPopper className="h-4 w-4" />
                <span className="hidden sm:inline">Jubiläum</span>
                <span className="sm:hidden">Jubil.</span>
              </TabsTrigger>
              <TabsTrigger value="warning" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <AlertTriangle className="h-4 w-4" />
                <span className="hidden sm:inline">Abmahnung</span>
                <span className="sm:hidden">Abmahn.</span>
              </TabsTrigger>
              <TabsTrigger value="vehicle" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <Car className="h-4 w-4" />
                <span className="hidden sm:inline">Fahrzeug</span>
                <span className="sm:hidden">KFZ</span>
              </TabsTrigger>
              <TabsTrigger value="safety" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Sicherheit</span>
                <span className="sm:hidden">Sicher.</span>
              </TabsTrigger>
              <TabsTrigger value="key_handover" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <Key className="h-4 w-4" />
                <span className="hidden sm:inline">Schlüssel</span>
                <span className="sm:hidden">Schl.</span>
              </TabsTrigger>
              <TabsTrigger value="templates" className="flex items-center gap-2 text-xs sm:text-sm py-2">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Vorlagen</span>
                <span className="sm:hidden">Vorl.</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="employment" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("employment")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <EmploymentContractForm />
              {canSeeContracts && <ContractsList templateType="employment_contract" />}
            </TabsContent>

            <TabsContent value="amendment" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("amendment")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <ContractAmendmentForm />
              {canSeeContracts && <ContractsList templateType="contract_amendment" />}
            </TabsContent>

            <TabsContent value="certificate" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("certificate")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <CertificateForm />
              {canSeeContracts && <ContractsList templateType="certificate" />}
            </TabsContent>

            <TabsContent value="anniversary" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("anniversary")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <AnniversaryCertificateForm />
              {canSeeContracts && <ContractsList templateType="anniversary" />}
            </TabsContent>

            <TabsContent value="warning" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("warning")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <WarningForm />
              {canSeeContracts && <ContractsList templateType="warning" />}
            </TabsContent>

            <TabsContent value="vehicle" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("vehicle")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <VehicleAssignmentForm />
              {canSeeContracts && <ContractsList templateType="vehicle_assignment" />}
            </TabsContent>

            <TabsContent value="safety" className="space-y-6">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenPreview("safety")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <SafetyInstructionForm />
              {canSeeContracts && <ContractsList templateType="safety_instruction" />}
            </TabsContent>

            <TabsContent value="key_handover" className="space-y-6">
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenPreview("key_handover")}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Mustervorschau
                </Button>
              </div>
              <KeyHandoverForm />
              {canSeeContracts && <ContractsList templateType="key_handover" />}
            </TabsContent>

            <TabsContent value="templates" className="space-y-6">
              <DocumentTemplateEditor />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Mustervorschau - {
                previewType === "employment" ? "Arbeitsvertrag" :
                previewType === "amendment" ? "Vertragsänderung" :
                previewType === "certificate" ? "Zertifikat" :
                previewType === "anniversary" ? "Jubiläumsurkunde" :
                previewType === "vehicle" ? "Fahrzeugüberlassung" :
                previewType === "safety" ? "Sicherheitsunterweisung" :
                previewType === "key_handover" ? "Schlüssel-/Transponder-Übergabe" :
                "Abmahnung"
              }
            </DialogTitle>
          </DialogHeader>
          <TemplatePreview type={previewType} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
